import React, { useEffect, useState } from "react";
import { Button, Modal, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Input from "components/Input";

import { useAppDispatch } from "store/hooks";
import {
  handleCreateWorkingAreaRequest,
  handleGetDealers,
} from "store/modules/Logistics/thunk.store";
import {
  COVERAGE_AREA_TYPE,
  CoverageAreaType,
  isDealerMapType,
} from "types/coverageAreaType";
import * as Styled from "./styles";

interface AddNewAreaData {
  mapLink: string;
  name: string;
}

interface AddNewAreaModalProps {
  dealerId: string;
  isOpen: boolean;
  onClose: () => void;
}

const AddNewAreaModal: React.FC<AddNewAreaModalProps> = ({
  dealerId,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<AddNewAreaData>();

  const [selectedType, setSelectedType] = useState<CoverageAreaType>(
    COVERAGE_AREA_TYPE.DEALER_MAP,
  );

  const dispatch = useAppDispatch();

  const handleSetCoverageType = (newType: CoverageAreaType) => {
    setSelectedType(() => {
      if (newType === COVERAGE_AREA_TYPE.DEALER_MAP) {
        setValue("name", "Nova área");
      } else if (newType === COVERAGE_AREA_TYPE.DEALER_STORE) {
        setValue("name", "PORTARIA");
      } else {
        setValue("name", "ENTREGADOR");
      }

      return newType;
    });
  };

  const handleSubmitForm = async (data: AddNewAreaData) => {
    const createWorkingArea = await dispatch(
      handleCreateWorkingAreaRequest({
        dealerId,
        name: data.name,
        type: selectedType,
        ...(isDealerMapType(selectedType) ? { mapLink: data.mapLink } : {}),
      }),
    ).unwrap();

    await dispatch(
      handleGetDealers({
        dealerId,
        coverageAreaId: createWorkingArea.id,
      }),
    ).unwrap();

    onClose();
  };

  useEffect(() => {
    reset();
  }, [isOpen, reset]);

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Styled.Content>
        <Typography variant="h6" mb={3}>
          {t("addNewCoverageArea")}
        </Typography>
        <Typography variant="subtitle1" mb={3}>
          1 - Selecione o tipo da área:
          <Styled.CheckBoxesContainer>
            <Styled.CheckBoxContainer>
              <Styled.CheckBox
                checked={selectedType === COVERAGE_AREA_TYPE.DEALER_MAP}
                onChange={() => handleSetCoverageType(COVERAGE_AREA_TYPE.DEALER_MAP)}
              />
              <Styled.CheckBoxLabel>
                Criar área com MAPA DE COBERTURA
              </Styled.CheckBoxLabel>
            </Styled.CheckBoxContainer>
            <Styled.CheckBoxContainer>
              <Styled.CheckBox
                checked={selectedType === COVERAGE_AREA_TYPE.DEALER_STORE}
                onChange={() =>
                  handleSetCoverageType(COVERAGE_AREA_TYPE.DEALER_STORE)
                }
              />
              <Styled.CheckBoxLabel>
                Criar área de PORTARIA
              </Styled.CheckBoxLabel>
            </Styled.CheckBoxContainer>
            <Styled.CheckBoxContainer>
              <Styled.CheckBox
                checked={selectedType === COVERAGE_AREA_TYPE.DRIVER_STORE}
                onChange={() =>
                  handleSetCoverageType(COVERAGE_AREA_TYPE.DRIVER_STORE)
                }
              />
              <Styled.CheckBoxLabel>
                Criar área exclusiva para ENTREGADOR
              </Styled.CheckBoxLabel>
            </Styled.CheckBoxContainer>
          </Styled.CheckBoxesContainer>
        </Typography>
        <Styled.FormContainer onSubmit={handleSubmit(handleSubmitForm)}>
          {isDealerMapType(selectedType) && (
            <>
              <Typography variant="subtitle1" mb={3}>
                2 - {t("createEditMapUsingYourAccountOn")}{" "}
                <a
                  href="https://www.google.com/maps/d/u/0/"
                  rel="noreferrer"
                  target="_blank"
                  color="inherit"
                >
                  Google My Maps
                </a>
              </Typography>
              <Typography variant="subtitle1">
                3 - {t("copyAndPasteLinkInTheFieldBelow")}
              </Typography>
              <Styled.TypeLinkContainer>
                <Input
                  name="mapLink"
                  error={errors.mapLink}
                  label="Google My Maps"
                  placeholder="https://www.google.com/maps/d/u/0/edit?mid=312312smnjbnneknNA32sE4&usp=sharing"
                  register={register}
                  fullWidth
                  registerOptions={{
                    required: t("requiredField"),
                    pattern: {
                      value:
                        /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/,
                      message: t("invalidUrl"),
                    },
                  }}
                  sx={{ mb: 3 }}
                  InputLabelProps={{ shrink: true }}
                />
              </Styled.TypeLinkContainer>
            </>
          )}

          <Typography variant="subtitle1" mb={3}>
            {isDealerMapType(selectedType) ? "4" : "2"} - {t("giveThisAreaName")}
          </Typography>
          <Input
            name="name"
            error={errors.name}
            label={t("areaName")}
            placeholder="Campinas"
            register={register}
            fullWidth
            registerOptions={{
              required: t("requiredField"),
            }}
            sx={{
              mb: 3,
            }}
            defaultValue="Nova área"
            InputLabelProps={{ shrink: true }}
          />

          <Styled.ActionsContainer>
            <Button color="inherit" variant="contained" onClick={onClose}>
              {t("back")}
            </Button>
            <Button
              sx={{ ml: 1 }}
              color="secondary"
              variant="contained"
              type="submit"
            >
              {t("createArea")}
            </Button>
          </Styled.ActionsContainer>
        </Styled.FormContainer>
      </Styled.Content>
    </Modal>
  );
};

export default AddNewAreaModal;
